import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { LuGlobe, LuMonitor, LuAlertTriangle } from 'react-icons/lu';
import { browserTimeZone, formatInZone, zoneOffsetLabel } from './reminderFormat';

// "What time is it where this reminder runs?" next to the viewer's own clock, so an
// admin outside Vietnam (or on a laptop set to another zone) can check a schedule at
// a glance. Ticks every 30 s. Warns when this computer's clock is off from the
// server's — then the times on the page are off by as much.

const TICK_MS = 30 * 1000;
const DRIFT_WARN_MS = 2 * 60 * 1000;

const ReminderZoneClock = ({ timeZone, serverNow }) => {
  const [now, setNow] = useState(() => new Date());
  // Fixed when the server answered: how far this computer's clock is from the server's.
  const [drift] = useState(() => (serverNow ? Date.now() - new Date(serverNow).getTime() : 0));

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), TICK_MS);
    return () => clearInterval(id);
  }, []);

  const local = browserTimeZone();
  const sameZone = zoneOffsetLabel(local, now) === zoneOffsetLabel(timeZone, now);

  return (
    <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-x-4 tw-gap-y-1 tw-text-[12px] tw-text-slate-600" aria-live="off">
      <span className="tw-inline-flex tw-items-center tw-gap-1">
        <LuGlobe size={13} className="tw-text-slate-400" />
        Now in {timeZone}: <strong className="tw-font-semibold tw-text-slate-800">{formatInZone(now, timeZone, true)}</strong>
        <span className="tw-text-slate-400">({zoneOffsetLabel(timeZone, now)})</span>
      </span>
      {!sameZone && (
        <span className="tw-inline-flex tw-items-center tw-gap-1">
          <LuMonitor size={13} className="tw-text-slate-400" />
          Your browser ({local}): {formatInZone(now, local, true)}
          <span className="tw-text-slate-400">({zoneOffsetLabel(local, now)})</span>
        </span>
      )}
      {Math.abs(drift) > DRIFT_WARN_MS && (
        <span role="status" className="tw-inline-flex tw-items-center tw-gap-1 tw-text-amber-700 tw-font-semibold">
          <LuAlertTriangle size={13} />
          This computer&apos;s clock is {Math.round(Math.abs(drift) / 60000)} min {drift > 0 ? 'ahead of' : 'behind'} the server
        </span>
      )}
    </div>
  );
};

ReminderZoneClock.propTypes = {
  timeZone: PropTypes.string.isRequired,
  serverNow: PropTypes.string,
};

export default ReminderZoneClock;
